import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CatalogOptionGroup } from '@/lib/draft-order-client';
import { isSingleChoiceGroup, type DraftLineOption } from './cart-draft';

interface OptionGroupSelectorProps {
  group: CatalogOptionGroup;
  selected: DraftLineOption[];
  currencySymbol: string;
  onChange: (next: DraftLineOption[]) => void;
}

/**
 * One option group, rendered as the choice it actually is.
 *
 * A max-of-one group behaves like a radio (picking B replaces A) and a multi-choice group like
 * checkboxes that stop accepting input at `maxChoices`. Getting this wrong is how an operator ends
 * up with two sizes on one pizza — so the constraint is enforced here, not just validated later.
 */
export function OptionGroupSelector({ group, selected, currencySymbol, onChange }: OptionGroupSelectorProps) {
  const { t } = useTranslation('conversations');
  const single = isSingleChoiceGroup(group);
  const min = group.minChoices ?? (group.required ? 1 : 0);
  const max = group.maxChoices ?? (single ? 1 : group.options.length);

  const chosenInGroup = selected.filter((option) => option.groupId === group.id);
  const isChosen = (optionId: string) => chosenInGroup.some((option) => option.optionId === optionId);
  const atLimit = chosenInGroup.length >= max;
  const unsatisfied = chosenInGroup.length < min;

  const toggle = (optionId: string) => {
    const option = group.options.find((candidate) => candidate.id === optionId);
    if (!option) return;

    const others = selected.filter((entry) => entry.groupId !== group.id);
    const entry: DraftLineOption = {
      groupId: group.id,
      optionId: option.id,
      groupName: group.name,
      optionName: option.name,
      priceDelta: option.priceDelta ?? 0,
    };

    if (single) {
      onChange([...others, entry]);
      return;
    }

    if (isChosen(optionId)) {
      onChange(selected.filter((e) => !(e.groupId === group.id && e.optionId === optionId)));
      return;
    }
    if (atLimit) return;
    onChange([...selected, entry]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{group.name}</span>
        <span className={cn('text-xs', unsatisfied ? 'text-destructive' : 'text-muted-foreground')}>
          {group.required
            ? t('cart.options.required', { count: min })
            : max < group.options.length
              ? t('cart.options.upTo', { count: max })
              : t('cart.options.optional')}
        </span>
      </div>

      <div className="grid gap-1.5">
        {group.options.map((option) => {
          const chosen = isChosen(option.id);
          const disabled = !chosen && !single && atLimit;
          return (
            <button
              key={option.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(option.id)}
              className={cn(
                'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                chosen
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-background text-foreground hover:bg-accent',
                disabled && 'cursor-not-allowed opacity-50 hover:bg-background',
              )}
            >
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center border',
                    single ? 'rounded-full' : 'rounded',
                    chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {chosen && <Check className="h-3 w-3" />}
                </span>
                {option.name}
              </span>
              {Boolean(option.priceDelta) && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {option.priceDelta! > 0 ? '+' : ''}
                  {currencySymbol}
                  {option.priceDelta!.toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
